/**
 * The card contracts (Vendor saved card): what a card provider's failure
 * may carry, the error the identity server and the checkout raise
 * (`IdentityError`, the seller kit's `JourneyError`), and a card capture's
 * boundary schemas, a saved card as a buyer's details give it
 * (`CardSummary`) and the transport a client captures one through.
 * `@libs/buyer-identity-client` re-exports them.
 */
import { z } from "zod";
import type { CardBrandId } from "./card-brands";

// Provider failures carry only allowlisted metadata, never response bodies,
// validation messages, aliases or card values.
export const cardResponseFieldSchema = z.enum([
  "response",
  "id",
  "pan",
  "cvc",
  "pan_alias",
  "cvc_alias",
  "cvc_status",
  "last4",
  "card_brand",
  "exp_month",
  "exp_year",
]);
export const cardProviderFailureSchema = z.object({
  operation: z.enum(["GET", "POST", "PATCH"]),
  status: z.number().int().min(100).max(599),
  invalidFields: z.array(cardResponseFieldSchema).max(11).optional(),
});
export class IdentityError extends Error {
  constructor(
    public code: string,
    public cardProvider?: z.infer<typeof cardProviderFailureSchema>,
  ) {
    super(code);
  }
}

export const captureStartSchema = z
  .object({ cardId: z.string().min(1).max(100).optional() })
  .strict();
// Opaque bearer aliases only. Numeric aliases can collide and do not prove
// possession; they are generated only on the private execution route.
export const opaqueAliasSchema = z
  .string()
  .regex(/^tok_(sandbox|live)_[A-Za-z0-9]{16,100}$/);
export const captureCompleteSchema = z
  .object({
    captureId: z.uuid(),
    panAlias: opaqueAliasSchema.optional(),
    cvcAlias: opaqueAliasSchema,
    expiryMonth: z.number().int().min(1).max(12).optional(),
    expiryYear: z.number().int().min(2026).max(2100).optional(),
  })
  .strict();
export type CaptureComplete = z.infer<typeof captureCompleteSchema>;
/** The longest a card capture lives: `beginCardCapture` caps the VGS
 *  token's own life at it. */
export const CARD_CAPTURE_MAX_SECONDS = 300;
export type CardCapture = {
  id: string;
  expiresAt?: string;
  vaultId: string;
  environment: "sandbox" | "live";
  routeId: string;
  accessToken: string;
  refresh: boolean;
};

/** `brandId` is computed when a saved card is read, from the vault's brand
 *  string (`cardBrandIdOf`, `./card-brands`); no column holds it. */
export type CardSummary = {
  id: string;
  brand: string;
  brandId: CardBrandId;
  last4: string;
  expiryMonth: number;
  expiryYear: number;
  cvcExpiresAt?: string | null;
};

export type CardTransport = {
  beginCardCapture(cardId?: string): Promise<CardCapture>;
  completeCardCapture(input: CaptureComplete): Promise<CardSummary>;
};
